import React from "react";
import { Button, Dialog, StatusPill } from "@/ds";
import type { OwnerTenantRuleParameters, RuleParamKey, RuleValuePreview } from "@/api";
import { useLang, useT } from "../i18n";
import { formatRuleMoney } from "./rule-parameter-display";

export function RuleValuesPreviewDialog({ data, preview, busy, onClose, onSave }: {
  data: OwnerTenantRuleParameters; preview: RuleValuePreview; busy: boolean;
  onClose: () => void; onSave: () => void;
}) {
  const t = useT();
  const { lang } = useLang();
  const label = (key: RuleParamKey) => t(`businesses.rules.param.${key}`, undefined, key);
  const shown = (key: RuleParamKey, value: number | boolean) => typeof value === "boolean"
    ? t(value ? "businesses.rules.yes" : "businesses.rules.no")
    : data.parameters.find((p) => p.key === key)?.unit === "eur_cents" ? formatRuleMoney(value, lang) : String(value);
  return <Dialog open title={t("ruleValues.preview.title")} onClose={busy ? undefined : onClose}
    footer={<div className="management-actions management-actions--end">
      <Button variant="ghost" disabled={busy} onClick={onClose}>{t("common.cancel")}</Button>
      <Button variant="primary" disabled={busy} loading={busy} onClick={onSave}>{t("businesses.rules.save")}</Button>
    </div>}>
    <div className="stack-sm reqventos-rule-preview">
      <p>{t("businesses.rules.openRequests")}</p>
      <dl className="reqventos-rule-preview__diff">
        {preview.changed_keys.map((key) => <div key={key}><dt>{label(key)}</dt>
          <dd>{shown(key, data.values[key])} → <strong>{shown(key, preview.values[key])}</strong></dd></div>)}
      </dl>
      <h3>{t("ruleValues.preview.impact")}</h3>
      <p className="subtle">{t("ruleValues.preview.method", { count: preview.impact.sampled_requests, limit: preview.impact.limit })}</p>
      {preview.impact.keys.map((impact) => <section key={impact.key} className="stack-xs">
        <h4>{label(impact.key)}</h4>
        <StatusPill tone={impact.status === "computed" ? "info" : "warning"} label={t(`ruleValues.preview.${impact.status}`)} />
        {impact.status === "unavailable" ? <p>{t(`ruleValues.preview.${impact.reason}`)}</p>
          : <p>{t("ruleValues.preview.crossings", { changed: impact.changed_requests, up: impact.below_to_at_or_above, down: impact.at_or_above_to_below })}</p>}
        <p className="subtle">{t("ruleValues.preview.sample", { comparable: impact.comparable_requests, unavailable: impact.unavailable_requests, inapplicable: impact.inapplicable_requests })}</p>
      </section>)}
      <h3>{t("ruleValues.preview.customerText")}</h3>
      <p className="subtle">{t("ruleValues.preview.conditional")}</p>
      {preview.customer_texts.map((text) => <blockquote key={`${text.key}:${text.template_id}`} lang="de">{text.text_de}</blockquote>)}
      {preview.customer_texts_unavailable.map(({ key }) => <p key={key} className="subtle">{label(key)}: {t("ruleValues.preview.noText")}</p>)}
    </div>
  </Dialog>;
}
